import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RuleIcon from '@mui/icons-material/Rule';
import { deletePolicy, getPolicies } from '../../../../api/endpoints/corporate';
import type { CorporatePolicy } from '../../../../api/types/corporate';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { PolicyFormDialog } from '../PolicyFormDialog';

export default function PolicyListPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CorporatePolicy | null>(null);
  const [deleting, setDeleting] = useState<CorporatePolicy | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({ queryKey: ['corporate', 'policies'], queryFn: () => getPolicies() });
  const rows: CorporatePolicy[] = query.data?.policies ?? [];

  const deleteMutation = useMutation({
    mutationFn: (policyID: number) => deletePolicy(policyID),
    onSuccess: () => {
      enqueueSnackbar('Politika silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['corporate', 'policies'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Seyahat Kural Setleri (Politikalar)</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Politika
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Politika Adı</TableCell>
              <TableCell>Uçak Maks. Bütçe</TableCell>
              <TableCell>Otel Günlük Maks. Bütçe</TableCell>
              <TableCell>Sınıf İzni</TableCell>
              <TableCell>Onay</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı politika bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.policyID} hover>
                <TableCell>{row.policyName}</TableCell>
                <TableCell>{row.flightMaximumBudget}</TableCell>
                <TableCell>{row.hotelDailyMaxBudget}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    {row.canUseBusinessClass && <Chip size="small" label="Business" />}
                    {row.canUseFirstClass && <Chip size="small" label="First" />}
                    {!row.canUseBusinessClass && !row.canUseFirstClass && (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={row.sendApproval ? 'warning' : 'default'}
                    label={row.sendApproval ? 'Onaya Gönderilir' : 'Otomatik'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="İstisnalar">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/corporate/policy/${row.policyID}/exceptions`)}
                      aria-label="İstisnalar"
                    >
                      <RuleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <PolicyFormDialog open={createOpen} policy={null} onClose={() => setCreateOpen(false)} />
      <PolicyFormDialog open={!!editing} policy={editing} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={!!deleting}
        title="Politikayı sil"
        description={`"${deleting?.policyName ?? ''}" politikasını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.policyID)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
